import { Button, CircularProgress, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IntegrationService } from '../../../../services/integration.service';
import BusinessCentralLogo from './logo.png';

export interface BusinessCentralConnectorResponse {
  connected: boolean;
  error?: string;
  url?: string;
  nextState?: 'start' | 'code' | 'error' | 'connected';
}

export default function BusinessCentralConnector(props: { afterDisconnect?: () => void }) {
  const location = useLocation();

  const [state, setState] = useState<'start' | 'code' | 'error' | 'connected'>('start');
  const [errorDescription, setErrorDescription] = useState<string>('');
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const code = queryParams.get('code');
    // const error = queryParams.get('error');

    if (!waiting) {
      setWaiting(true);
      IntegrationService.connect('businessCentral', { state, code })
        .then((data: any) => {
          if (data.url) {
            // If a URL is provided for OAuth, redirect the user.
            window.location.href = data.url;
            // No need to call setWaiting(false) or setState here as the page will navigate away.
          } else {
            // If no URL, update component state normally.
            setState(data.nextState ?? 'start');
            if (data.error) {
              setErrorDescription(data.error);
            }
            setWaiting(false);
          }
        })
        .catch((err) => {
          console.log(err);

          setState('error');
          setErrorDescription(err.message);
          setWaiting(false);
        });
    }
  }, [location, state]); // Added `state` to dependency array

  const disconnect = () => {
    setWaiting(true);
    IntegrationService.disconnect('businessCentral').finally(() => {
      setState('start');
      setWaiting(false);
      if (props.afterDisconnect) {
        props.afterDisconnect();
      }
    });
  };

  const goBack = () => {
    // You might want to use react-router-dom's useNavigate hook for more robust navigation
    window.history.back();
  };

  return (
    <Grid container p={2}>
      <img src={BusinessCentralLogo} height={50} />
      {waiting ? (
        <Grid container mt={2}>
          <CircularProgress />
        </Grid>
      ) : state === 'connected' ? (
        <Grid container mt={2} mb={2} direction="column" spacing={2}>
          <Typography variant="subtitle1" color="green">
            Connected.
          </Typography>
          <Grid container justifyContent="end">
            <Button variant="contained" color="error" onClick={disconnect}>
              Disconnect
            </Button>
          </Grid>
        </Grid>
      ) : state === 'error' ? (
        <Grid container mt={2} mb={2} direction="column" spacing={2}>
          <Grid item>
            <Typography variant="subtitle1" color="error">
              Error: {errorDescription || 'An unknown error occurred.'}
            </Typography>
          </Grid>
          <Grid item>
            <Grid container justifyContent="end">
              <Button variant="outlined" onClick={goBack}>
                Back
              </Button>
            </Grid>
          </Grid>
        </Grid>
      ) : null}
    </Grid>
  );
}
